import { FC, ReactNode } from "react"

import { TournamentParticipant } from "types/tournament.types"

import { formatTournamentMoney, usdCentsToCurrencyMinor } from "util/formatter"

interface TournamentLeaderboardProps {
  participants: TournamentParticipant[]
  currency?: string
  currencyRate?: number
}

export const TournamentLeaderboard: FC<TournamentLeaderboardProps> = ({ participants, currency, currencyRate }) => {
  const columns: { header: string; cell: (participant: TournamentParticipant, index: number) => ReactNode }[] = [
    {
      header: "Rank",
      cell: (participant, index) => (
        <div className="flex items-center">
          {index < 3 ? <span className="text-2xl mr-2">{index === 0 ? "🥇" : index === 1 ? "🥈" : "🥉"}</span> : null}
          <span className="text-sm font-medium text-gray-900 dark:text-white">#{participant.current_rank || index + 1}</span>
        </div>
      )
    },
    {
      header: "Player",
      cell: (participant) => (
        <div className="text-sm font-medium text-gray-900 dark:text-white">{participant.player_username || participant.player_id}</div>
      )
    },
    {
      header: "Score",
      cell: (participant) => (
        <div className="text-sm font-bold text-gray-900 dark:text-white">{Number(participant.final_score).toLocaleString()}</div>
      )
    },
    {
      header: "Spins",
      cell: (participant) => <span className="text-sm text-gray-500 dark:text-gray-400">{participant.total_spins}</span>
    },
    {
      header: "Wagered",
      cell: (participant) => (
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {formatTournamentMoney(usdCentsToCurrencyMinor(participant.total_wagered, currencyRate), currency)}
        </span>
      )
    },
    {
      header: "Won",
      cell: (participant) => (
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {formatTournamentMoney(usdCentsToCurrencyMinor(participant.total_won, currencyRate), currency)}
        </span>
      )
    },
    {
      header: "Prize",
      cell: (participant) =>
        participant.prize_won ? (
          <span className="text-sm font-medium text-green-600 dark:text-green-400">Awarded</span>
        ) : (
          <span className="text-sm text-gray-400">-</span>
        )
    }
  ]

  return (
    <div className="bg-white dark:bg-[#0C0C1A] rounded-xl border border-[rgba(0,0,0,0.05)] dark:border-[rgba(255,255,255,0.1)] shadow-sm">
      <div className="p-6 border-b border-[rgba(0,0,0,0.05)] dark:border-[rgba(255,255,255,0.1)]">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Live Leaderboard</h3>
          <span className="text-sm text-gray-500 dark:text-gray-400">Auto-refreshes every 10 seconds</span>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 dark:bg-[rgba(139,92,246,0.05)]">
            <tr>
              {columns.map((column) => (
                <th
                  key={column.header}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-[#0C0C1A] divide-y divide-[rgba(0,0,0,0.05)] dark:divide-[rgba(255,255,255,0.1)]">
            {participants.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                  No participants yet
                </td>
              </tr>
            ) : (
              participants.map((participant, index) => (
                <tr key={participant.id} className="hover:bg-gray-50 dark:hover:bg-[rgba(139,92,246,0.05)] transition-colors">
                  {columns.map((column) => (
                    <td key={column.header} className="px-6 py-4 whitespace-nowrap">
                      {column.cell(participant, index)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
